import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Calendar, Clock, User, Home, ArrowRight } from 'lucide-react';
import api from '../services/api';

const BookingSuccessPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [booking, setBooking] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        // Intentar obtener la reserva.
        // Nota: Esto puede requerir autenticación o un endpoint público si el usuario no está logueado.
        // Si el usuario acaba de pagar, asumimos que tiene permisos o la sesión está activa.
        let response = await api.get(`appointments/appointments/${id}/`);
        setBooking(response.data);
      } catch (err) {
        console.error('Error fetching booking:', err);
        // Si hubo un 401/403 por tema de sesión, reintentar contra endpoint público
        try {
          const alt = await api.get(`appointments/appointments/${id}/public/`, { headers: { Authorization: undefined as any } });
          setBooking(alt.data);
        } catch (err2) {
          console.error('Fallback public booking fetch failed:', err2);
          setError('No pudimos cargar los detalles de la reserva, pero si recibiste el correo de confirmación, todo está listo.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBooking();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-dark"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow-xl rounded-2xl overflow-hidden">
          <div className="bg-green-50 p-8 text-center border-b border-green-100">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-serif font-bold text-gray-900 mb-2">
              ¡Reserva Confirmada!
            </h1>
            <p className="text-gray-600 max-w-md mx-auto">
              Tu cita ha sido agendada exitosamente. Hemos enviado un correo con los detalles.
            </p>
          </div>

          <div className="p-8">
            {booking ? (
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Detalles de la Cita</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex items-start space-x-3">
                      <Calendar className="w-5 h-5 text-primary-dark mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Fecha</p>
                        <p className="font-medium text-gray-900">{booking.date}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Clock className="w-5 h-5 text-primary-dark mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Horario</p>
                        <p className="font-medium text-gray-900">{booking.start_time} - {booking.end_time}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <User className="w-5 h-5 text-primary-dark mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Profesional</p>
                        <p className="font-medium text-gray-900">
                          {(() => {
                            const prof = booking.professional_detail || booking.professional;
                            if (!prof) return 'Por asignar';
                            const full = prof.full_name || `${prof.first_name || ''} ${prof.last_name || ''}`.trim();
                            return full || 'Por asignar';
                          })()}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <User className="w-5 h-5 text-primary-dark mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Cliente</p>
                        <p className="font-medium text-gray-900">{booking.customer_name || 'Cliente'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {booking.appointment_services && booking.appointment_services.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Servicios Agendados</h3>
                    <div className="bg-white border rounded-lg divide-y">
                      {booking.appointment_services.map((service: any) => (
                        <div key={service.id} className="p-4 flex justify-between items-center">
                          <div>
                            <p className="font-medium text-gray-900">{service.name}</p>
                            <p className="text-sm text-gray-500">{service.duration_minutes} min</p>
                          </div>
                          <p className="font-medium text-gray-900">${parseInt(service.price).toLocaleString('es-CL')}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">{error || 'No se encontraron detalles de la reserva.'}</p>
              </div>
            )}

            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/" 
                className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <Home className="w-5 h-5 mr-2" />
                Volver al Inicio
              </Link>
              <Link 
                to="/profile" 
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-dark hover:bg-primary-dark/90 shadow-lg hover:shadow-xl transition-all"
              >
                Ver Mis Reservas
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccessPage;
